import { useCallback, useEffect } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_HIGH,
  CommandListener,
  EditorState,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ARROW_DOWN_COMMAND,
} from 'lexical';

type EventHandler = (ev: React.KeyboardEvent, editorState: EditorState) => boolean;
type OnKeyDownPluginProps = {
  onEnter?: EventHandler;
  onEscape?: EventHandler;
  propagateArrowNavigation?: boolean;
  mentionMenuOpen: boolean;
};

export function OnKeyDownPlugin({
  onEnter: onEnterProp,
  onEscape: onEscapeProp,
  propagateArrowNavigation = true,
  mentionMenuOpen,
}: OnKeyDownPluginProps) {
  const [editor] = useLexicalComposerContext();

  const onEnter: CommandListener<React.KeyboardEvent> = useCallback(
    (ev: React.KeyboardEvent) => {
      if (mentionMenuOpen) {
        return false;
      }
      return onEnterProp?.(ev, editor.getEditorState()) ?? false;
    },
    [editor, onEnterProp, mentionMenuOpen],
  );
  const onEscape: CommandListener<React.KeyboardEvent> = useCallback(
    (ev: React.KeyboardEvent) => {
      return onEscapeProp?.(ev, editor.getEditorState()) ?? false;
    },
    [editor, onEscapeProp],
  );

  const onArrowNavigation = useCallback(
    (ev: React.KeyboardEvent) => {
      if (!propagateArrowNavigation) {
        ev.stopPropagation();
        return true;
      }
      return false;
    },
    [propagateArrowNavigation],
  );

  useEffect(() => {
    return editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_HIGH);
  }, [editor, onEnter]);

  useEffect(() => {
    return editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_HIGH);
  }, [editor, onEscape]);

  // prevent navigating out of the cell
  useEffect(() => {
    return editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onArrowNavigation, COMMAND_PRIORITY_HIGH);
  }, [editor, onArrowNavigation]);

  useEffect(() => {
    return editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onArrowNavigation, COMMAND_PRIORITY_HIGH);
  }, [editor, onArrowNavigation]);

  useEffect(() => {
    return editor.registerCommand(KEY_ARROW_UP_COMMAND, onArrowNavigation, COMMAND_PRIORITY_HIGH);
  }, [editor, onArrowNavigation]);

  useEffect(() => {
    return editor.registerCommand(KEY_ARROW_DOWN_COMMAND, onArrowNavigation, COMMAND_PRIORITY_HIGH);
  }, [editor, onArrowNavigation]);

  return null;
}
